/** @file quadOverlay: every drawn part's tile quad, filled at 25 % in its own colour and outlined 1 CSS px. */
import { Color3 } from '@babylonjs/core/Maths/math.color.js';
import { CreatePlane } from '@babylonjs/core/Meshes/Builders/planeBuilder.js';
import { Mesh } from '@babylonjs/core/Meshes/mesh.js';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData.js';
import type { Scene } from '@babylonjs/core/scene.js';
import type { Profile } from '@zilo/pose3d-format/types/profile';
import type { View } from '@zilo/pose3d-render-core/camera/viewOf';
import type { Camera, ImpostorList } from '@zilo/pose3d-render-core/contract';
import { IMPOSTOR_LAYOUT as L } from '@zilo/pose3d-render-core/layout/impostorLayout';
import { boneMaterial } from './boneMaterial';
import { displayPoint } from './displayPoint';
import { matrixRows, type V3 } from './matrixRows';
import { quadColorOf } from './quadColorOf';
import { quadGeomOf } from './quadGeomOf';
import { QUAD_PALETTE } from './quadPalette';
import { segmentQuad } from './segmentQuad';
import { thinBatch } from './thinBatch';
import type { QuadEdit } from './viewportCtx';
import { VIEWPORT_GROUPS } from './viewportGroups';
import { VIEWPORT_LAYERS } from './viewportLayers';

/**
 * The placed impostors, the puppet's drawables (by index; the id picks the colour), the hovered quad and the quad under a dragged
 * file (drawable indices, -1 none), `all` (the quads view is on: every quad; off: only the drop target), a running
 * quad drag, the camera, view and `px` (scene units per CSS px).
 */
export type QuadsInput = {
  list: ImpostorList; count: number; drawables: readonly { id: string }[]; hovered: number; drop: number; all: boolean; edit: QuadEdit | null;
  camera: Camera; profile: Profile; view: View; px: number;
};

/** `update(input)` once per frame (null hides both meshes); `fills()` and `edges()` count what was drawn; meshes for tests. */
export type QuadOverlay = { fill: Mesh; outline: Mesh; update(input: QuadsInput | null): void; fills(): number; edges(): number };

const COLORS = QUAD_PALETTE.map((h) => Color3.FromHexString(h));
/** Fill and edge alpha and edge width (CSS px): plain, hovered (brighter), under a dragged file (brightest). */
const LOOK = { plain: [0.25, 0.85, 1], hover: [0.45, 1, 1.5], drop: [0.55, 1, 2.5] } as const;

type P2 = readonly [number, number];

/** The quad's four edges as corner index pairs (tl-tr, tr-br, br-bl, bl-tl). */
const EDGES = [[0, 1], [1, 2], [2, 3], [3, 0]] as const;

/** Drawable `d`'s look this frame: the drop target, hovered, plain (the quads view on) or not drawn. */
function lookOf(m: QuadsInput, d: number): readonly [number, number, number] | null {
  if (d === m.drop) return LOOK.drop;
  if (d === m.hovered) return LOOK.hover;
  return m.all ? LOOK.plain : null;
}

/** One unit triangle (0,0) (1,0) (0,1): a thin-instance matrix maps it onto any screen triangle. */
function unitTriangle(scene: Scene): Mesh {
  const mesh = new Mesh('viewport-quad-fill', scene);
  Object.assign(new VertexData(), { positions: [0, 0, 0, 1, 0, 0, 0, 1, 0], indices: [0, 1, 2] }).applyToMesh(mesh);
  return mesh;
}

/** Scratch (display frame): triangle / edge points, two edge vectors and the view's back vector. */
const P: V3 = [0, 0, 0];
const Q: V3 = [0, 0, 0];
const R: V3 = [0, 0, 0];
const X: V3 = [0, 0, 0];
const Y: V3 = [0, 0, 0];
const B: V3 = [0, 0, 0];

/**
 * Two meshes in the marks group of the overlay (bones' unlit, always-on-top material): the fills as two thin-instance
 * triangles per quad (exact for any quad render-core draws, two-point stretched ones included) and the 1 CSS px
 * outline as camera-facing segments, both under the bones and the selected outline (`alphaIndex` 0 and 1). The quad
 * is render-core's (`quadGeomOf`, a running drag applied), so it lies exactly on the drawn picture.
 */
export function quadOverlay(scene: Scene): QuadOverlay {
  const fillMesh = unitTriangle(scene);
  const edgeMesh = CreatePlane('viewport-quad-outline', { size: 1 }, scene);
  for (const [i, m] of [fillMesh, edgeMesh].entries()) {
    Object.assign(m, { material: boneMaterial(scene), layerMask: VIEWPORT_LAYERS.overlay, renderingGroupId: VIEWPORT_GROUPS.marks, isPickable: false, alwaysSelectAsActiveMesh: true, alphaIndex: i });
  }
  const fills = thinBatch(fillMesh);
  const edges = thinBatch(edgeMesh);
  const tri = (v: View, a: P2, b: P2, c: P2, rgba: readonly number[]): void => {
    displayPoint(P, a[0], a[1], v); displayPoint(Q, b[0], b[1], v); displayPoint(R, c[0], c[1], v);
    [X[0], X[1], X[2]] = [Q[0] - P[0], Q[1] - P[1], Q[2] - P[2]];
    [Y[0], Y[1], Y[2]] = [R[0] - P[0], R[1] - P[1], R[2] - P[2]];
    matrixRows(fills.matrices(), fills.add(rgba), X, Y, B, P);
  };
  const edge = (m: QuadsInput, a: P2, b: P2, rgba: readonly number[], width: number): void => {
    displayPoint(P, a[0], a[1], m.view); displayPoint(Q, b[0], b[1], m.view);
    segmentQuad(edges.matrices(), edges.add(rgba), P, Q, B, width * m.px);
  };
  const draw = (m: QuadsInput, i: number): void => {
    const d = m.list[i * L.stride + L.drawable] ?? -1;
    const look = lookOf(m, d);
    if (look === null) return;
    const col = COLORS[quadColorOf(m.drawables[d]?.id ?? '')] ?? Color3.White();
    const c = quadGeomOf(m.list, i, m.camera, m.profile, m.edit).corners;
    tri(m.view, c[0], c[1], c[3], [col.r, col.g, col.b, look[0]]);
    tri(m.view, c[2], c[3], c[1], [col.r, col.g, col.b, look[0]]);
    for (const [e, f] of EDGES) edge(m, c[e], c[f], [col.r, col.g, col.b, look[1]], look[2]);
  };
  return {
    fill: fillMesh,
    outline: edgeMesh,
    update: (m) => {
      fills.reset();
      edges.reset();
      if (m !== null) {
        const { back } = m.view.basis;
        [B[0], B[1], B[2]] = [back[0], back[2], back[1]];
        for (let i = 0; i < m.count; i++) draw(m, i);
      }
      fills.commit();
      edges.commit();
    },
    fills: () => fills.count() / 2,
    edges: () => edges.count(),
  };
}
